import * as React from 'react'

interface Boom {
  id: number
  element: HTMLDivElement
  position: {
    x: string
    y: string
  }
  particles: Particle[]
}

interface Particle {
  id: number
  element: HTMLDivElement
  opacity: number
  destination: {
    x: number
    y: number
    z: number
  }
  position: {
    x: number
    y: number
    z: number
  }
  rotation: {
    x: number
    y: number
    z: number
  }
  scale: number
  rotateSpeed: {
    x: number
    y: number
    z: number
  }
}

export function Confetti({
  amount = 20,
  rate = 0.02, // be careful with this number
  radius = 10,
  colors = [
    '#A02372',
    '#E0DE41',
    '#AAD068',
    '#CE3681',
    '#EBE140',
    '#86C8E3',
    '#E6B04D',
    '#DF8A3F',
    '#F1E240',
    '#EEE240',
    '#D52A6B',
    '#62AADA',
    '#89CAE4',
  ],
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const booms = React.useRef<Boom[]>([])

  React.useEffect(() => {
    let isMounted = true

    function explode() {
      const element = document.createElement('div')

      const boom: Boom = {
        id: Math.random(),
        position: {
          x: `${(5 + 90 * Math.random()).toFixed(2)}%`,
          y: `${(15 * Math.random()).toFixed(2)}%`,
        },
        particles: [],
        element,
      }

      element.style.position = 'absolute'
      element.style.left = boom.position.x
      element.style.top = boom.position.y

      if (ref.current) {
        ref.current.appendChild(element)
      }

      for (let i = 0; i < amount; i++) {
        const color = colors[Math.floor(Math.random() * colors.length)] ?? colors[0]

        const element = document.createElement('div')
        element.style.width = `${radius}px`
        element.style.height = `${radius}px`
        element.style.background = color
        element.style.position = 'absolute'
        element.style.left = `0px`
        element.style.top = `0px`

        const particle: Particle = {
          id: Math.random(),
          element,
          opacity: 1,
          position: {
            x: 0,
            y: 0,
            z: 0,
          },
          destination: {
            x: randFloat(-0.5, 0.5) * (radius * 100) * Math.random(),
            y: randFloat(-0.66, 0.25) * (radius * 100) * Math.random(),
            z: randFloat(-0.5, 0.5) * (radius * 50) * Math.random(),
          },
          rotation: {
            x: Math.random() * 360,
            y: Math.random() * 360,
            z: Math.random() * 360,
          },
          scale: randFloat(1, 2.5),
          rotateSpeed: {
            x: randFloat(6, 12),
            y: randFloat(6, 12),
            z: randFloat(6, 12),
          },
        }

        element.style.setProperty('opacity', particle.opacity.toString())
        element.style.setProperty(
          'transform',
          `translate3d(${particle.position.x}px, ${particle.position.y}px, 0) rotateX(${particle.rotation.x}deg) rotateY(${particle.rotation.y}deg) scale(${particle.scale})`,
        )

        boom.element.appendChild(element)

        boom.particles.push(particle)
      }

      booms.current = [...booms.current, boom]
    }

    function frame() {
      if (Math.random() <= rate) {
        explode()
      }

      const toRemoveBooms = new Set<number>()

      // oxlint-disable-next-line typescript/prefer-for-of
      for (let i = 0; i < booms.current.length; i++) {
        const boom = booms.current[i]
        const toRemoveParticles = new Set<number>()

        // oxlint-disable-next-line typescript/prefer-for-of
        for (let k = 0; k < boom.particles.length; k++) {
          const particle = boom.particles[k]

          particle.destination.y += randFloat(3, 4)

          const speed = 25
          const speedX = (particle.destination.x - particle.position.x) / speed
          const speedY = (particle.destination.y - particle.position.y) / speed
          const speedZ = (particle.destination.z - particle.position.z) / speed

          particle.position.x += speedX
          particle.position.y += speedY
          particle.position.z += speedZ

          particle.rotation.x += particle.rotateSpeed.x
          particle.rotation.y += particle.rotateSpeed.y
          particle.rotation.z += particle.rotateSpeed.z

          if (particle.opacity > 0) {
            particle.opacity -= randFloat(0.005, 0.01)
          }
          if (particle.scale > 0) {
            // oxlint-disable-next-line unicorn/no-zero-fractions
            particle.scale -= randFloat(0.0, 0.01)
          }

          if (particle.opacity <= 0) {
            toRemoveParticles.add(particle.id)

            if (boom.element) {
              boom.element.removeChild(particle.element)
            }
          } else {
            particle.element.style.setProperty('opacity', particle.opacity.toString())
            particle.element.style.setProperty(
              'transform',
              `translate3d(${particle.position.x}px, ${particle.position.y}px, ${particle.position.z}px) rotateX(${particle.rotation.x}deg) rotateZ(${particle.rotation.z}deg) rotateY(${particle.rotation.y}deg) scale(${particle.scale})`,
            )
          }

          if (toRemoveParticles.size) {
            boom.particles = boom.particles.filter(particle => !toRemoveParticles.has(particle.id))
          }
        }

        if (boom.particles.length === 0) {
          toRemoveBooms.add(boom.id)

          if (ref.current) {
            ref.current.removeChild(boom.element)
          }
        }
      }

      if (toRemoveBooms.size) {
        booms.current = booms.current.filter(boom => !toRemoveBooms.has(boom.id))
      }
      if (isMounted) {
        requestAnimationFrame(frame)
      }
    }

    requestAnimationFrame(frame)

    return () => {
      isMounted = false
    }
  }, [amount, colors, radius, rate])

  return <div className="pointer-events-none absolute inset-0" ref={ref} />
}

function randFloat(min: number, max: number) {
  return Math.random() * (max - min) + min
}
